import { useState } from 'react';
import { AlertTriangle, OctagonAlert } from 'lucide-react';
import type { OrcamentoDto, OrcamentoExibicao, OrcamentoReformaExibicao, OrcamentoSimuladoResponse } from '@triade/contracts';
import { useSimularOrcamentoEm } from '@/api/orcamentos';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ApiError } from '@/lib/api';
import { formatarData, formatarMoeda } from '@/lib/format';
import { NOTA_INFORMATIVO, REGIME_ROTULO, SeloEstimada } from '@/components/reforma/reforma-ui';

export interface AvisoReforma {
  code: string;
  message: string;
  fronteira: string;
  etapa: string;
  paraDualTransicao?: boolean;
}

export interface AvisosReformaProps {
  /** resp.avisosReforma do orcamento gravado (a simulacao nao devolve avisos de regime). */
  avisos: AvisoReforma[] | undefined;
  /** Reforma da exibicao atual (regime da data de faturamento). */
  reformaAtual: OrcamentoReformaExibicao | undefined;
  /** Corpo atual do orcamento (o mesmo enviado a simulacao). Nulo = formulario incompleto. */
  dto: OrcamentoDto | null;
}

/** Avisos de regime (ORCAMENTO_CRUZA_REGIME) e a simulacao "este orcamento em <ano da fronteira>". Valores vem da API. */
export function AvisosReforma({ avisos, reformaAtual, dto }: AvisosReformaProps) {
  const [fronteiraSimulada, setFronteiraSimulada] = useState<string | null>(null);
  const simular = useSimularOrcamentoEm();
  const [simulado, setSimulado] = useState<OrcamentoSimuladoResponse | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  if (!avisos || avisos.length === 0) return null;

  async function simularEm(fronteira: string) {
    if (!dto) return;
    setErro(null);
    setFronteiraSimulada(fronteira);
    try {
      setSimulado(await simular.mutateAsync({ ...dto, dataFaturamento: fronteira }));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'falha de comunicação com a API');
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid="avisos-reforma">
      {avisos.map((a) =>
        a.paraDualTransicao ? (
          <section key={a.code} role="alert" aria-label="Orçamento cruza a reforma" className="rounded-2xl border border-destructive/40 bg-danger-bg px-[18px] py-4">
            <div className="flex items-center gap-2 font-semibold text-danger-ink">
              <OctagonAlert aria-hidden="true" className="size-[18px]" />
              Orçamento cruza a mudança de regime da reforma
            </div>
            <div className="mt-1 text-small text-danger-ink">{a.message}</div>
            <div className="mt-1 text-caption text-ink-muted">código {a.code}</div>
            <Button type="button" variant="outline" size="sm" className="mt-3" disabled={!dto || simular.isPending} onClick={() => void simularEm(a.fronteira)}>
              Simular este preço em {a.fronteira.slice(0, 4)}
            </Button>
          </section>
        ) : (
          <section key={a.code} role="alert" aria-label="Aviso de regime" className="flex items-start gap-2.5 rounded-2xl border border-warning-dot/40 bg-warning-bg px-[18px] py-3.5">
            <AlertTriangle aria-hidden="true" className="mt-px size-[18px] shrink-0 text-warning-ink" />
            <div className="text-small text-warning-ink">
              {a.message}
              <div className="text-caption text-ink-muted">código {a.code}</div>
            </div>
          </section>
        ),
      )}
      {erro && (
        <p role="alert" className="text-small text-danger-ink">
          {erro}
        </p>
      )}

      <Dialog open={fronteiraSimulada !== null && simulado !== null} onOpenChange={(o) => !o && setFronteiraSimulada(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Este orçamento em {simulado?.exibicao.reforma?.etapa ?? ''}</DialogTitle>
            <DialogDescription>
              Simulação com a data de faturamento em {fronteiraSimulada ? formatarData(fronteiraSimulada) : ''}. Nada é gravado.
            </DialogDescription>
          </DialogHeader>
          {simulado && (
            <ComparacaoReforma atual={reformaAtual} simulado={simulado.exibicao} />
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFronteiraSimulada(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ComparacaoReforma({ atual, simulado }: { atual: OrcamentoReformaExibicao | undefined; simulado: OrcamentoExibicao }) {
  const sim = simulado.reforma;
  return (
    <table data-testid="comparacao-reforma" className="w-full text-left text-small">
      <thead className="text-caption text-ink-muted">
        <tr>
          <th scope="col" className="py-1.5 font-medium">Valor</th>
          <th scope="col" className="py-1.5 text-right font-medium">Atual{atual ? ` (${atual.etapa})` : ''}</th>
          <th scope="col" className="py-1.5 text-right font-medium">Simulado{sim ? ` (${sim.etapa})` : ''}</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-t border-border/60">
          <td className="py-1.5">+ CBS/IBS{sim?.informativo ? ' (informativo)' : ''}</td>
          <td className="tabular py-1.5 text-right">{formatarMoeda(atual?.cbsIbsTotal)}</td>
          <td className="tabular py-1.5 text-right">{formatarMoeda(sim?.cbsIbsTotal)}</td>
        </tr>
        <tr className="border-t border-border/60">
          <td className="py-1.5 font-semibold">Total da NF</td>
          <td className="tabular py-1.5 text-right font-semibold">{formatarMoeda(atual?.nfTotal)}</td>
          <td className="tabular py-1.5 text-right font-semibold">{formatarMoeda(sim?.nfTotal)}</td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3} className="pt-3 text-caption text-ink-muted">
            {sim?.aliquotaEstimada && <SeloEstimada className="mr-2" />}
            {sim?.informativo && NOTA_INFORMATIVO}
            {sim && <span className="ml-2">Regime: {REGIME_ROTULO[sim.regime]}</span>}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}
